// Where a desktop URL lands when opened on a phone. A phone never sees the
// desktop shell (CustomerShell redirects every route below 768px), so each
// desktop destination needs a companion home; anything the phone does not
// have lands on Today rather than on a guess.
const MAP: Array<[prefix: string, to: string]> = [
  ["/record", "/companion/record"],
  ["/meetings", "/companion/meetings"],
  ["/todos", "/companion/todos"],
  // The list and one task (a bell item's "Review" / "Open task" links here).
  ["/tasks", "/companion/tasks"],
  // The one Schedules page (?id= opens one: the bell's schedule items link there).
  ["/schedules", "/companion/schedules"],
  // "On their own": the morning line links here (?agent=<t> kept).
  ["/initiatives", "/companion/initiatives"],
  // Learned (what Lattice learned about the workspace): the Monday clause links here (?view=&week= kept).
  ["/learned", "/companion/learned"],
  ["/agents", "/companion/chat"],
  ["/chat", "/companion/chat"],
  ["/memories", "/companion/chat"],
  // The old per-agent desktop URLs (still redirected on the desktop).
  ["/mail", "/companion/chat"],
  ["/files", "/companion/chat"],
  ["/routines", "/companion/chat"],
  ["/desktops", "/companion/chat"],
  ["/projects", "/companion/projects"],
  ["/deliverables", "/companion/deliverables"],
  ["/skills", "/companion/skills"],
  ["/subagents", "/companion/subagents"],
  // The list, a run and a job; the editor and the data sources are desktop pages.
  ["/workflows", "/companion/workflows"],
  ["/shared", "/companion/vault"],
  ["/settings", "/companion/settings"],
  // A command waiting for the operator's OK keeps its id; /machines itself is in Settings.
  ["/machines", "/companion/settings"],
  // Desktop-only screens.
  ["/handoffs", "/companion/today"],
  ["/connections", "/companion/today"],
  // Code › Apps and one app's page (apps spec, ui-plan §0.1): before /code; the slug is kept.
  ["/code/apps", "/companion/apps"],
  ["/code", "/companion/today"],
];

// Agent pages that name an agent (/agents/<t>/mail, /chat/<t>,
// /memories/<t>, /mail/<t>, ...) open that agent's conversation.
const AGENT_PREFIXES = new Set(["/agents", "/chat", "/memories", "/mail", "/files", "/routines", "/desktops"]);

/**
 * The companion PATHNAME for a desktop pathname. It never carries a hash:
 * the caller keeps the location's own hash (CustomerShell redirects with
 * `{ pathname: companionPathFor(pathname), hash }`), so /settings#whatsapp
 * lands on /companion/settings#whatsapp and the settings page picks its
 * tab from the hash on phones too.
 */
export function companionPathFor(pathname: string): string {
  for (const [prefix, to] of MAP) {
    if (pathname !== prefix && !pathname.startsWith(prefix + "/")) continue;
    // A blueprint keeps its id and tab (/subagents/<t>/<tab> has the same shape there); a task keeps its id.
    if (prefix === "/subagents" || prefix === "/tasks" || prefix === "/code/apps") return to + pathname.slice(prefix.length);
    if (prefix === "/machines" && pathname.startsWith("/machines/approvals/")) return "/companion" + pathname;
    // A workflow run and a job keep their id: the run view and the job page work at phone width.
    if (prefix === "/workflows" && (pathname.startsWith("/workflows/runs/") || (pathname.startsWith("/workflows/jobs/") && pathname !== "/workflows/jobs/new"))) {
      return to + pathname.slice(prefix.length);
    }
    const agent = AGENT_PREFIXES.has(prefix) ? pathname.slice(prefix.length + 1).split("/")[0] : "";
    return agent ? `${to}/${encodeURIComponent(decodeSegment(agent))}` : to;
  }
  return "/companion/today";
}

/**
 * Where a whole desktop URL lands on a phone: its companion pathname, the
 * query kept (Subagents keeps its tab and filters there, /subagents?tab=runs
 * opens Runs on the phone too). Home's control board names its agent in the
 * query (/dashboard?agent=julia), and the phone has no board: that opens
 * the agent's conversation (a blueprint's is under Subagents; ChatThread
 * sends it on). A job opens its page there too; the brief form opens
 * Activity; the workflow editor and the data sources are desktop pages, so
 * they open the list with a one-line notice (?desktop=editor|data).
 */
export function companionLocationFor(pathname: string, search: string): { pathname: string; search: string } {
  const workflows = "/companion/workflows";
  if (pathname === "/workflows/jobs" || pathname === "/workflows/jobs/new") return { pathname: workflows, search: "?tab=activity" };
  if (pathname === "/workflows/data" || pathname.startsWith("/workflows/data/")) return { pathname: workflows, search: "?desktop=data" };
  if (pathname === "/workflows/saved-results" || pathname.startsWith("/workflows/saved-results/")) return { pathname: workflows, search: "?desktop=data" };
  const second = pathname.startsWith("/workflows/") ? pathname.split("/")[2] : "";
  if (second && second !== "runs" && second !== "jobs") return { pathname: workflows, search: "?desktop=editor" };
  if (pathname === "/dashboard") {
    const agent = new URLSearchParams(search).get("agent");
    if (agent) return { pathname: `/companion/chat/${encodeURIComponent(agent)}`, search: "" };
  }
  return { pathname: companionPathFor(pathname), search };
}

/**
 * The web's own phone redirect (CustomerShell): companionLocationFor, except
 * that a workflow shared with this login (the bell's Look,
 * /workflows/new?import=1&shared=<id>) lands on the list with ITS notice
 * (?desktop=shared: it waits, add it on a computer), not the editor's. Not
 * in companionLocationFor: the iPhone app's port pins that table.
 */
export function phoneLocationFor(pathname: string, search: string): { pathname: string; search: string } {
  if (pathname === "/workflows/new" && new URLSearchParams(search).get("shared")) return { pathname: "/companion/workflows", search: "?desktop=shared" };
  return companionLocationFor(pathname, search);
}

function decodeSegment(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}
